import React, { useState, useEffect } from 'react';
import { ChevronDownRegular, HomeRegular, DocumentRegular, OpenRegular, PersonRegular } from '@fluentui/react-icons';
import propertyService from '../services/propertyService';
import { useLanguage } from '../contexts/LanguageContext';
import { useTextSize } from '../contexts/TextSizeContext';
import config from '../../config';

export default function PropertyInfo({ isOpen, onToggle, contextVersion }) {
  const [propertyData, setPropertyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { t } = useLanguage();
  const { currentTextSize } = useTextSize();

  // Helper function to get user initials
  const getUserInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  useEffect(() => {
    if (isOpen) {
      fetchPropertyData();
    }
  }, [isOpen, contextVersion]);

  const fetchPropertyData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Check if property ID is available before fetching
      if (!propertyService.getPropertyId()) {
        setPropertyData(null);
        setError(null); // Clear any previous error
        setLoading(false);
        return;
      }
      
      const response = await propertyService.getProperty();
      setPropertyData(response.data);
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch property data:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border-b border-gray-300">
      <button 
        className={`flex items-center justify-between w-full ${currentTextSize.cardPadding} hover:bg-gray-50 transition-colors`}
        onClick={onToggle}
      >
        <div className="flex items-center gap-2">
          <HomeRegular className="h-4 w-4 text-gray-600" />
          <h3 className={`${currentTextSize.headerText} font-semibold text-gray-900`}>{t('propertyInfo.title')}</h3>
        </div>
        <ChevronDownRegular
          className={`h-4 w-4 text-gray-500 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className={`px-4 pb-4 ${currentTextSize.spacing}`}>
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className={`ml-2 ${currentTextSize.bodyText} text-gray-600`}>{t('propertyInfo.loadingPropertyData')}</span>
            </div>
          )}

          {!loading && !propertyData && !error && (
            <div className={`bg-blue-50 border border-blue-200 rounded-lg ${currentTextSize.padding}`}>
              <div className="flex items-start gap-3">
                <HomeRegular className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className={`${currentTextSize.labelText} font-semibold text-blue-900 mb-1`}>
                    {t('propertyInfo.noPropertyInfoTitle')}
                  </h4>
                  <p className={`${currentTextSize.bodyText} text-blue-700`}>
                    {t('propertyInfo.noPropertyInfoMessage')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className={`bg-red-50 border border-red-200 rounded-lg ${currentTextSize.padding}`}>
              <div className="flex items-center">
                <div className={`text-red-600 ${currentTextSize.bodyText}`}>
                  <strong>{t('propertyInfo.errorLoadingProperty')}</strong> {error}
                </div>
              </div>
              <button 
                onClick={fetchPropertyData}
                className={`mt-2 ${currentTextSize.bodyText} text-red-600 hover:text-red-700 underline`}
              >
                {t('propertyInfo.tryAgain')}
              </button>
            </div>
          )}

          {propertyData && !loading && !error && (
            <div className="space-y-4">
              {/* Property Name */}
              <div className="flex items-start gap-3">
                <HomeRegular className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <h4 className={`${currentTextSize.headerText} font-semibold text-gray-900 break-words`}>
                    {propertyData.name}
                  </h4>
                </div>
              </div>

              {/* Property Details */}
              <div className="space-y-2">
                {propertyData.progress_status && (
                  <div className={`flex items-start gap-2 ${currentTextSize.bodyText}`}>
                    <DocumentRegular className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-gray-600">{t('propertyInfo.status')}</span>
                      <span className="text-gray-900 font-medium ml-1 break-words">{propertyData.progress_status}</span>
                    </div>
                  </div>
                )}

                {propertyData.notes && (
                  <div className={`flex items-start gap-2 ${currentTextSize.bodyText}`}>
                    <DocumentRegular className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-gray-600">{t('propertyInfo.notes')}</span>
                      <p className="text-gray-900 font-medium ml-1 mt-0.5 whitespace-pre-line break-words">{propertyData.notes}</p>
                    </div>
                  </div>
                )}

                {/* Mapped Users */}
                {(propertyData.user_1_name || propertyData.user_2_name) && (
                  <div className={`flex items-start gap-2 ${currentTextSize.bodyText}`}>
                    <PersonRegular className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-gray-600">{t('propertyInfo.mappedUsers')}</span>
                      <div className="mt-1.5 space-y-2">
                        {propertyData.user_1_name && (
                          <div className="flex items-center gap-2">
                            {propertyData.user_1_picture_url ? (
                              <img 
                                src={propertyData.user_1_picture_url} 
                                alt={propertyData.user_1_name}
                                className="w-8 h-8 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-semibold">
                                {getUserInitials(propertyData.user_1_name)}
                              </div>
                            )}
                            <span className="text-gray-900">{propertyData.user_1_name}</span>
                          </div>
                        )}
                        {propertyData.user_2_name && (
                          <div className="flex items-center gap-2">
                            {propertyData.user_2_picture_url ? (
                              <img 
                                src={propertyData.user_2_picture_url} 
                                alt={propertyData.user_2_name}
                                className="w-8 h-8 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-semibold">
                                {getUserInitials(propertyData.user_2_name)}
                              </div>
                            )}
                            <span className="text-gray-900">{propertyData.user_2_name}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Action Button */}
              <button 
                onClick={() => window.open(`${config.APP_BASE_URL}/stammdaten/property/${propertyData.id}`, '_blank')}
                className={`w-full flex items-center justify-center text-blue-600 hover:text-blue-700 hover:bg-blue-50 ${currentTextSize.padding} rounded ${currentTextSize.bodyText} font-medium transition-colors border border-blue-200`}
              >
                <OpenRegular className="h-4 w-4 mr-2" />
                {t('propertyInfo.viewPropertyInDoNexus')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
